import { useClickOutside, useDebouncedEffect } from '@react-hookz/web';
import { useQuery } from '@tanstack/react-query';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import { LocateMeButton } from '~/components/LocateMeButton';
import Modal from '~/components/Modal/Modal';
import { useGlobal } from '~/hooks/useGlobal';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { useSetMapCenter } from '~/hooks/useSetMapCenter';
import { modalsService } from '~/services/Modals';
import { mainStore, orderStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';
import { ModalType } from '../Modal/interface';
import { ModalBody, ModalFooter, ModalHeader } from '../ModalComponents';
import AddressAutocomplete from '../ModalDeliveryAddress/AddressAutocomplete';
import AddressAutocompleteDropDown from '../ModalDeliveryAddress/AddressAutocompleteDropDown';
import { REDIRECT_QUERY_NAME } from '../ModalDeliveryAddress/constants';
import { deliveryAddressStore } from '../ModalDeliveryAddress/DeliveryAddressStore';
import Geocoding from '../ModalDeliveryAddress/Geocoding/Geocoding';
import GMap from '../ModalDeliveryAddress/GMap/GMap';
import geocoderRequest from '../ModalDeliveryAddress/geocoderRequest';
import SaveButton from '../ModalEditAddressDetails/SaveButton';

import styles from './ModalEditAddressLocation.module.scss';

interface AutocompleteResponse {
  data: {
    result: {
      address: string;
      postcode: null | string;
      location: {
        latitude: number;
        longitude: number;
      };
    } | null;
  };
}

const ModalEditAddressLocation = observer(() => {
  const { t } = useTranslation();

  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState(false);
  const [addressInputText, setAddressInputText] = useState('');
  const [addressToSearch, setAddressToSearch] = useState('');
  const [autocompletePlaceId, setAutocompletePlaceId] = useState<string>();
  const [searchParams] = useSearchParams();
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);
  const editableAddressId = searchParams.get(MODAL_EDIT_ADDRESS_PARAM);
  const editableAddress = userStore.isAuthorized
    ? null
    : userStore.deliveryAddress?.address1;

  const addressFormRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useGlobal();
  const geocodingRequestTimeoutRef =
    useRef<Nullable<ReturnType<typeof setTimeout>>>(null);
  const { closeModal, openModal, goBack } = useModal();
  useSetMapCenter(userStore.deliveryAddress);

  useEffect(() => {
    if (isAutoCompleteOpen) {
      addressInputRef.current?.focus();
    }
  }, [isAutoCompleteOpen]);

  useEffect(() => {
    userStore.resetAddressSearchSessionToken();
  }, []);

  useEffect(() => {
    setAddressInputText(deliveryAddressStore.inputAddressValue);
  }, [deliveryAddressStore.inputAddressValue]);

  useDebouncedEffect(
    () => setAddressToSearch(addressInputText),
    [addressInputText],
    500,
  );

  useClickOutside(addressFormRef, () => {
    setIsAutoCompleteOpen(false);
    setAddressInputText(deliveryAddressStore.inputAddressValue);
  });

  useEffect(() => {
    return () => {
      mainStore.alerts.pop();
      deliveryAddressStore.resetStore();
    };
  }, []);

  useEffect(() => {
    setAutocompletePlaceId(undefined);
  }, [deliveryAddressStore.deliveryAddress?.placeId]);

  useEffect(() => {
    if (editableAddressId) {
      const editableAddress = userStore.addressList.find(
        (item) => item.id === editableAddressId,
      );

      if (editableAddress) {
        const point = {
          lat: editableAddress.latitude,
          lng: editableAddress.longitude,
        };

        deliveryAddressStore.setMapCenter(point);
        deliveryAddressStore.setSelectedZone(
          deliveryAddressStore.getSelectedPolygon(point),
        );
      }
    }
  }, [editableAddressId]);

  const {
    data: { result: autocompleteResult },
    isFetching,
  } = useQuery({
    enabled: !!userStore.addressSearchSessionToken && !!autocompletePlaceId,
    queryKey: ['autocomplete-get-place', autocompletePlaceId],
    initialData: {
      result: null,
    },
    queryFn: () =>
      geocoderRequest
        .post<AutocompleteResponse>('/autocomplete', {
          input: '',
          placeId: autocompletePlaceId,
          sessionToken: userStore.addressSearchSessionToken,
          countryCode: company.config.countryCode || '',
        })
        .then(({ data: { data } }) => data),
  });

  useEffect(() => {
    if (!autocompleteResult) {
      return;
    }

    const point = {
      lat: autocompleteResult.location.latitude,
      lng: autocompleteResult.location.longitude,
    };

    const selectedZone = deliveryAddressStore.getSelectedPolygon(point);

    deliveryAddressStore.setMapCenter(point);
    deliveryAddressStore.setSelectedZone(selectedZone);

    deliveryAddressStore.setDeliveryAddress({
      zip: '',
      country: '',
      city: '',
      region: '',
      address1: '',
      address2: '',
      addressFloor: '',
      addressBld: '',
      addressApt: '',
      shortAddress: '',
      coordinates: point,
      placeId: mainStore.getPseudoId(),
      addressId: null,
      comment: '',
      instructions: [],
      type: 'home',
    });

    const cleanTimeout = () => {
      if (geocodingRequestTimeoutRef.current) {
        clearTimeout(geocodingRequestTimeoutRef.current);
        geocodingRequestTimeoutRef.current = null;
      }
    };
    if (selectedZone) {
      cleanTimeout();
      geocodingRequestTimeoutRef.current = setTimeout(() => {
        deliveryAddressStore.geocoding(false, {
          lng: autocompleteResult.location.longitude,
          lat: autocompleteResult.location.latitude,
        });
        geocodingRequestTimeoutRef.current = null;
      }, 100);
    } else {
      orderStore.requestETA();
    }
  }, [autocompleteResult, deliveryAddressStore.renderedZonesOnMap]);

  useEffect(() => {
    deliveryAddressStore.setIsLoading(isFetching);
  }, [isFetching]);

  const setAutocompleteActive = () => {
    setIsAutoCompleteOpen(true);
  };

  const handleCloseModal = () => {
    mainStore.alerts.pop();
    closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
    deliveryAddressStore.setSelectedZone(null);
  };

  const handleGoBack = () => {
    goBack([MODAL_EDIT_ADDRESS_PARAM]);
    deliveryAddressStore.setSelectedZone(null);
  };

  const handleOpenModal = (addressId: string) => {
    if (editableAddressId) {
      goBack();
    } else if (addressId) {
      openModal(
        ModalType.EditAddressDetails,
        ModalType.EditAddressLocation,
        {
          [MODAL_EDIT_ADDRESS_PARAM]: addressId,
          [REDIRECT_QUERY_NAME]: redirectNext || '',
        },
        false,
        true,
      );
    } else {
      closeModal([MODAL_EDIT_ADDRESS_PARAM, REDIRECT_QUERY_NAME]);
    }
  };

  const autocompleteInput = (
    <div
      ref={addressFormRef}
      className={classes([
        styles.autocompleteInputContainer,
        isAutoCompleteOpen && styles.addressInputContainerFocused,
        isAutoCompleteOpen && 'mobile__opened',
      ])}
    >
      <input
        className={styles.autocompleteInput}
        data-input="address"
        placeholder={
          addressInputText
            ? t(isAutoCompleteOpen ? 'typeLocation' : 'addressNotSet')
            : editableAddress || t('addressNotSet')
        }
        value={addressInputText}
        onChange={(e) => setAddressInputText(e.target.value)}
        onFocus={() => setAutocompleteActive()}
        disabled
        ref={addressInputRef}
      />
      <AddressAutocompleteDropDown
        isOpen={isAutoCompleteOpen}
        className={styles.dropDown}
      >
        <AddressAutocomplete
          searchSessionToken={userStore.addressSearchSessionToken}
          searchText={addressToSearch}
          onSelect={(placeId, address) => {
            setIsAutoCompleteOpen(false);
            if (placeId) {
              setAutocompletePlaceId(placeId);
              setAddressInputText(address);
              userStore.resetAddressSearchSessionToken();
            }
          }}
          onLoadingStateChange={(isLoading) =>
            deliveryAddressStore.setIsLoading(isLoading)
          }
          showSelectOnMap={false}
          rowClassName={styles.autocompleteRow}
        />
      </AddressAutocompleteDropDown>
    </div>
  );

  return (
    <Modal
      isShow
      className={styles.selectLocation}
      onBackdropDismiss={handleCloseModal}
    >
      <div className={styles.selectLocation}>
        <div className={styles.selectLocation__container}>
          <ModalHeader
            isVisibleBackButton={isMobile ? modalsService.hasHistory : false}
            className={styles.header}
            onBackPress={handleGoBack}
            onClose={handleCloseModal}
            title={t('checkout:address:title')}
          />

          <ModalBody className={styles.selectLocation__container__body}>
            <div className={styles.mapContainer}>
              <Geocoding />
              <GMap />
              {!company.isPudoOnly && (
                <>
                  {(isMobile || !addressInputText) && (
                    <div className={styles.address}>
                      <div>
                        {addressInputText ? addressInputText : t('movePin')}
                      </div>
                    </div>
                  )}
                  <Icon
                    type="pin"
                    size={company.match({
                      citydrinks: 44,
                      v3: 44,
                      default: 64,
                    })}
                    className={styles.mapPin}
                  />
                  {isMobile && <LocateMeButton />}
                </>
              )}
            </div>
          </ModalBody>

          <ModalFooter className={styles.selectLocation__container__footer}>
            {isMobile ? null : (
              <div className={styles.modalForm}>{autocompleteInput}</div>
            )}
            <div className={styles.ctaButtonContainer}>
              {isMobile ? null : <LocateMeButton />}
              <SaveButton
                shouldValidateBuilding
                className={styles.proceedButton}
                title={t('deliveryHere')}
                onClick={handleOpenModal}
              />
            </div>
          </ModalFooter>
        </div>
      </div>
    </Modal>
  );
});

export default ModalEditAddressLocation;
